'use client';

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Map, { Marker, NavigationControl, Popup, MapRef } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ArrowRight, Star, CheckCircle, X, Music } from 'lucide-react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { LANDING_COLORS } from '@/constants/landing-colors';
import { MAPBOX_TOKEN, useMapboxCss } from '@/lib/mapbox';
import { TYPE_COLORS, TYPE_ICONS, TYPE_LABELS, type DirectoryType } from '@/constants/directoryTypes';

interface Listing {
  id: string;
  name: string;
  slug: string;
  type: DirectoryType;
  city: string;
  address?: string;
  province?: string;
  latitude?: number | null;
  longitude?: number | null;
  rating?: number | null;
  review_count?: number;
  is_claimed?: boolean;
  is_premium?: boolean;
  is_promoted?: boolean;
  observations?: string | null;
}

interface DirectoryMapProps {
  listings: Listing[];
  onSelectListing?: (listing: Listing) => void;
  selectedType?: string;
  selectedCity?: string;
}

// Spain center
const SPAIN_CENTER = {
  latitude: 40.4168,
  longitude: -3.7038,
  zoom: 5.5,
};

// Madrid center
const MADRID_CENTER = {
  latitude: 40.4168,
  longitude: -3.7038,
  zoom: 11,
};

const colors = LANDING_COLORS.jams;

export default function DirectoryMap({ listings, onSelectListing, selectedType, selectedCity }: DirectoryMapProps) {
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const mapRef = useRef<MapRef>(null);
  const cssReady = useMapboxCss();
  // Filter listings that have coordinates
  const listingsWithCoords = useMemo(
    () => listings.filter(l => l.latitude && l.longitude),
    [listings],
  );

  // Calculate initial view based on listings
  const initialView = useMemo(() => {
    if (listingsWithCoords.length === 0) return SPAIN_CENTER;

    if (selectedCity?.toLowerCase() === 'madrid') return MADRID_CENTER;

    // If all in same city, center on that
    if (listingsWithCoords.length > 0) {
      const avgLat = listingsWithCoords.reduce((sum, l) => sum + (l.latitude || 0), 0) / listingsWithCoords.length;
      const avgLng = listingsWithCoords.reduce((sum, l) => sum + (l.longitude || 0), 0) / listingsWithCoords.length;
      return {
        latitude: avgLat,
        longitude: avgLng,
        zoom: listingsWithCoords.length === 1 ? 14 : 11,
      };
    }

    return SPAIN_CENTER;
  }, [listingsWithCoords, selectedCity]);

  // Update view when filters change
  useEffect(() => {
    mapRef.current?.flyTo({
      center: [initialView.longitude, initialView.latitude],
      zoom: initialView.zoom,
      duration: 1000,
    });
  }, [selectedType, selectedCity, initialView]);

  // Resize map when it mounts (fixes issues with modals/fixed positioning)
  useEffect(() => {
    const timer = setTimeout(() => {
      mapRef.current?.resize();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleMarkerClick = useCallback((listing: Listing) => {
    setSelectedListing(listing);
    onSelectListing?.(listing);

    if (listing.latitude && listing.longitude) {
      mapRef.current?.flyTo({
        center: [listing.longitude, listing.latitude + 0.003],
        zoom: 14.5,
        duration: 800,
      });
    }
  }, [onSelectListing]);

  const closePopup = useCallback(() => {
    setSelectedListing(null);
  }, []);
  if (!cssReady) return (
    <div className="w-full h-full bg-gray-100 animate-pulse rounded-2xl" />
  );
  if (!MAPBOX_TOKEN) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-2xl">
        <p className="text-gray-500">Configurando mapa...</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative" style={{ touchAction: 'pan-x pan-y' }}>
      <Map
        ref={mapRef}
        initialViewState={initialView}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        mapboxAccessToken={MAPBOX_TOKEN}
        mapLib={mapboxgl}
        dragPan={true}
        scrollZoom={true}
        doubleClickZoom={true}
        touchZoomRotate={true}
      >
        <NavigationControl position="bottom-right" showCompass={false} />

        {listingsWithCoords.map(listing => {
          const Icon = TYPE_ICONS[listing.type] || Music;
          const typeColor = TYPE_COLORS[listing.type] || colors.primary;
          const isSelected = selectedListing?.id === listing.id;

          return (
            <Marker
              key={listing.id}
              latitude={listing.latitude!}
              longitude={listing.longitude!}
              anchor="bottom"
              onClick={e => {
                e.originalEvent.stopPropagation();
                handleMarkerClick(listing);
              }}
            >
              <div
                className={`relative flex flex-col items-center cursor-pointer transition-transform hover:scale-110 active:scale-95 ${listing.is_promoted ? 'z-30' : ''}`}
              >
                {/* Promoted star badge */}
                {listing.is_promoted && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center shadow-sm z-10">
                    <Star className="w-2.5 h-2.5 text-white fill-white" />
                  </div>
                )}

                {/* Marker pill */}
                <div
                  className={`px-2.5 py-1.5 rounded-full shadow-lg border-2 transition-all flex items-center gap-1.5 ${
                    isSelected
                      ? 'bg-white scale-110 z-20'
                      : listing.is_promoted
                        ? 'bg-amber-50 hover:scale-105'
                        : 'bg-white hover:scale-105'
                  }`}
                  style={{
                    borderColor: isSelected
                      ? typeColor
                      : listing.is_promoted
                        ? '#F59E0B'
                        : 'white',
                    boxShadow: isSelected
                      ? `0 4px 12px -2px ${typeColor}40`
                      : listing.is_promoted
                        ? '0 4px 12px -2px rgba(245, 158, 11, 0.4)'
                        : undefined
                  }}
                >
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: typeColor }}
                  >
                    <Icon className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-[10px] font-bold text-gray-900 max-w-[100px] truncate">
                    {listing.name}
                  </span>
                  {listing.is_claimed && (
                    <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                  )}
                </div>

                {/* Pointer */}
                <div
                  className={`w-2.5 h-2.5 rotate-45 -mt-1 border-r-2 border-b-2 ${
                    listing.is_promoted ? 'bg-amber-50' : 'bg-white'
                  }`}
                  style={{
                    borderColor: isSelected
                      ? typeColor
                      : listing.is_promoted
                        ? '#F59E0B'
                        : 'white'
                  }}
                />
              </div>
            </Marker>
          );
        })}

        {/* Popup */}
        <AnimatePresence>
          {selectedListing && selectedListing.latitude && selectedListing.longitude && (
            <Popup
              latitude={selectedListing.latitude}
              longitude={selectedListing.longitude}
              offset={30}
              closeButton={false}
              closeOnClick={false}
              anchor="bottom"
              maxWidth="280px"
              className="z-50"
            >
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="bg-white rounded-xl overflow-hidden shadow-xl border border-gray-100"
              >
                {/* Header */}
                <div className="p-3 relative">
                  <button
                    onClick={closePopup}
                    className="absolute top-2 right-2 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-gray-500" />
                  </button>

                  <div className="flex items-start gap-3 pr-6">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${TYPE_COLORS[selectedListing.type]}10` }}
                    >
                      {(() => {
                        const Icon = TYPE_ICONS[selectedListing.type] || Music;
                        return <Icon className="w-5 h-5" style={{ color: TYPE_COLORS[selectedListing.type] }} />;
                      })()}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-bold text-gray-900 text-sm truncate">
                        {selectedListing.name}
                      </h4>
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                        style={{
                          backgroundColor: `${TYPE_COLORS[selectedListing.type]}10`,
                          color: TYPE_COLORS[selectedListing.type]
                        }}
                      >
                        {TYPE_LABELS[selectedListing.type]}
                      </span>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-center gap-1.5 mt-2 text-gray-500">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="text-[11px] truncate">
                      {selectedListing.address}, {selectedListing.city}
                    </span>
                  </div>

                  {/* Rating & Verified */}
                  <div className="flex items-center gap-3 mt-2">
                    {selectedListing.rating && (
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-400 fill-current" />
                        <span className="text-xs font-bold text-gray-900">{selectedListing.rating}</span>
                        <span className="text-[10px] text-gray-400">({selectedListing.review_count})</span>
                      </div>
                    )}
                    {selectedListing.is_claimed && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600">
                        <CheckCircle className="w-3 h-3" />
                        Verificado
                      </span>
                    )}
                  </div>

                  {/* Description preview */}
                  {selectedListing.observations && (
                    <p className="text-[11px] text-gray-500 mt-2 line-clamp-2">
                      {selectedListing.observations}
                    </p>
                  )}
                </div>

                {/* CTA */}
                <div className="px-3 pb-3">
                  <Button
                    asChild
                    className="w-full h-9 text-xs font-semibold rounded-lg"
                    style={{ backgroundColor: TYPE_COLORS[selectedListing.type] }}
                  >
                    <Link href={`/directorio/${selectedListing.slug}`}>
                      Ver perfil completo
                      <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </motion.div>
            </Popup>
          )}
        </AnimatePresence>
      </Map>

      {/* Empty state overlay - pointer-events-none to not block map */}
      {listingsWithCoords.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 backdrop-blur-sm pointer-events-none">
          <div className="text-center p-6">
            <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm font-medium">
              No hay establecimientos con ubicación
            </p>
            <p className="text-gray-400 text-xs mt-1">
              Prueba a cambiar los filtros
            </p>
          </div>
        </div>
      )}

      {/* Results count badge - pointer-events-none to not block map */}
      <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-md border border-gray-200 pointer-events-none">
        <span className="text-xs font-semibold text-gray-700">
          {listingsWithCoords.length} de {listings.length} en el mapa
        </span>
      </div>
    </div>
  );
}
